<template>
  <tr
    class="cycle-table-row cursor-pointer"
    :class="{
      'cycle-table-row--closed': isClosed,
      'cycle-table-row--muted': isSettingUp,
      'low-opacity': cycle.status === false,
    }"
    data-testid="bookclosure-project-list__row"
    @mouseenter="rowHovered = true"
    @mouseleave="rowHovered = false"
  >
    <template v-for="header in visibleHeaders">
      <td
        v-if="header.value !== 'action'"
        :key="header.value"
        class="px-4 single-line cycle-table-row__cell"
        :class="cellClass(header.value)"
        :data-testid="header.value === 'description' ? 'bookclosure-project-list__description-cell' : `cycles-page__cell--${header.value}`"
        @click="onRowClick"
      >
        <!-- Name -->
        <div
          v-if="header.value === 'description'"
          class="d-flex align-center cycle-table-row__name"
          style="min-width: 0;"
        >
          <strong class="text-truncate flex-shrink-1 font-weight-medium" style="min-width: 0;">
            {{ cycle.description }}
          </strong>
        </div>

        <!-- Period -->
        <span v-else-if="header.value === 'period'" data-testid="cycles-page__period-text" class="text-body-small period-text"  >
          {{ cycle.periodText || formatPeriodRange(cycle.from, cycle.to) }}
        </span>

        <!-- Location -->
        <div
          v-else-if="header.value === 'location'"
          class="cycle-table-row__location text-body-small"
          data-testid="cycles-page__location-cell"
        >
          <template v-if="primaryLocationName">
            <span
              class="cycle-table-row__location-name"
              data-testid="cycles-page__location-primary"
              :title="primaryLocationName"
            >
              {{ primaryLocationName }}
            </span>
            <v-tooltip
              v-if="extraLocationCount > 0"
              top
              content-class="tooltip-top"
              open-delay="500"
              data-testid="cycles-page__location-more-tooltip"
            >
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                  class="cycle-table-row__location-more"
                  data-testid="cycles-page__location-more-badge"
                >
                  +{{ extraLocationCount }}
                </span>
              </template>
              <div data-testid="cycles-page__location-more-list">
                <div
                  v-for="(name, idx) in extraLocationNames"
                  :key="`${name}-${idx}`"
                  data-testid="cycles-page__location-item"
                >
                  {{ name }}
                </div>
              </div>
            </v-tooltip>
          </template>
          <span v-else class="grey--text">—</span>
        </div>

        <!-- Status: creation badge (Setup Failed / Setting up) or Open / Closed -->
        <div
          v-else-if="header.value === 'status'"
          class="d-flex align-center cycle-table-row__status text-body-small"
          data-testid="cycles-page__status-cell"
        >
          <v-chip
            v-if="showCreationBadge"
            label
            class="flex-shrink-0 cycle-table-row__status-badge"
            :class="[
              statusConfig.textClass,
              statusConfig.badgeClass,
              { 'cycle-table-row__status-badge--clickable': isFailedSetup },
            ]"
            data-testid="bookclosure-project-list__cycle-status-badge"
            @click.stop="onCreationBadgeClick"
          >
            <img
              v-if="statusConfig.iconSrc"
              :src="statusConfig.iconSrc"
              alt=""
              class="cycle-table-row__status-icon cycle-table-row__status-icon--img"
            />
            <v-icon
              v-else
              x-small
              :class="statusConfig.textClass"
            >
              {{ statusConfig.icon }}
            </v-icon>
            {{ statusConfig.label }}
          </v-chip>
          <template v-else>
            <span
              class="cycle-table-row__status-dot"
              :class="isClosed ? 'cycle-table-row__status-dot--closed' : 'cycle-table-row__status-dot--open'"
              data-testid="cycles-page__status-dot"
            />
            <span data-testid="cycles-page__status-label">
              {{ isClosed ? 'Closed' : 'Open' }}
            </span>
          </template>
        </div>

        <!-- Progress -->
        <div
          v-else-if="header.value === 'progress'"
          class="cycle-table-row__progress text-body-small"
          data-testid="cycles-page__progress-cell"
        >
          <template v-if="isSettingUp">
            <v-skeleton-loader
              type="text"
              width="140"
              data-testid="cycles-page__progress-skeleton"
            />
          </template>
          <template v-else-if="isFailedSetup">
            <span class="grey--text" data-testid="cycles-page__progress-na">N/A</span>
          </template>
          <template v-else>
            <v-menu offset-x left open-on-hover open-delay="500">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="cycle-table-row__progress-activator my-1"
                  v-bind="attrs"
                  v-on="on"
                  data-testid="cycles-page__progress-activator"
                >
                  <div
                    class="cycle-table-row__progress-bar"
                    :class="{ 'cycle-table-row__progress-bar--closed': isClosed }"
                    data-testid="cycles-page__progress-bar"
                  >
                    <div
                      class="cycle-table-row__progress-fill"
                      :class="isClosed ? 'cycle-table-row__progress-fill--closed' : 'cycle-table-row__progress-fill--open'"
                      :style="{ width: `${completionPct}%` }"
                      data-testid="cycles-page__progress-fill"
                    />
                  </div>
                  <span
                    class="cycle-table-row__progress-pct ml-2"
                    data-testid="cycles-page__progress-pct"
                  >
                    {{ completionPct }}%
                  </span>
                </div>
              </template>
              <v-card class="shadow-off" data-testid="cycles-page__progress-details-card">
                <v-card-text class="py-2">
                  <span>Progress details</span>
                  <div>
                    <span>As</span>
                    <span
                      class="mx-1 font-weight-bold"
                      :class="isManager ? 'caccounting--text' : ''"
                    >
                      {{ isManager ? 'Manager' : 'General User' }}
                    </span>
                    <span>role</span>
                  </div>
                </v-card-text>
                <v-divider />
                <v-card-text class="py-2">
                  <table data-testid="cycles-page__progress-summary-table">
                    <caption class="d-none">Progress summary</caption>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold pr-2">{{ summary.count || 0 }}</td>
                        <td class="border-off">Total Task</td>
                      </tr>
                      <tr class="info--text">
                        <td class="font-weight-bold">{{ summary.pending || 0 }}</td>
                        <td class="border-off">Pending Submission</td>
                      </tr>
                      <tr class="warning--text">
                        <td class="font-weight-bold">{{ summary.pendingapproval || 0 }}</td>
                        <td class="border-off">Pending Approval</td>
                      </tr>
                      <tr class="success--text">
                        <td class="font-weight-bold">{{ summary.approved || 0 }}</td>
                        <td class="border-off">Closed Task</td>
                      </tr>
                    </tbody>
                  </table>
                </v-card-text>
              </v-card>
            </v-menu>
          </template>
        </div>

        <UpsiStatusCell
          v-else-if="header.value === 'upsi'"
          :upsi-state="displayedUpsiState"
          :row-hovered="rowHovered"
          :index="index"
          @view-details="$emit('upsi-view-details', cycle)"
        />

        <span v-else>{{ cycle[header.value] }}</span>
      </td>
    </template>

    <!-- Actions: primary (Close/Reopen/Retry) left of a fixed more-menu slot -->
    <td
      class="cycle-table-row__actions px-2"
      data-testid="bookclosure-project-list__action-cell"
      @click.stop
    >
      <div class="cycle-table-row__actions-inner">
        <div class="cycle-table-row__actions-primary">
          <template v-if="isFailedSetup && isManager">
            <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  class="cycle-table-row__failed-action"
                  data-testid="bookclosure-project-list__retry-btn"
                  @click.stop="$emit('retry-cycle-creation', cycle)"
                >
                  <img
                    src="/img/icons/Sync.svg"
                    alt="Retry"
                    data-testid="bookclosure-project-list__retry-icon"
                    class="retry-icon"
                  />
                </v-btn>
              </template>
              <span>Retry</span>
            </v-tooltip>
            <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  class="cycle-table-row__failed-action cycle-table-row__failed-action--danger"
                  data-testid="bookclosure-project-list__delete-btn"
                  @click.stop="$emit('delete-cycle', cycle)"
                >
                  <img
                    src="/img/icons/fbc-icons/DeleteCycle.svg"
                    alt="Delete"
                    data-testid="bookclosure-project-list__delete-icon"
                    class="delete-icon"
                  />
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </template>

          <template v-else-if="!isSettingUp && !isFailedSetup">
            <v-btn
              v-if="showCloseAction"
              small
              text
              class="cycle-table-row__cycle-action text-none"
              data-testid="bookclosure-project-list__close-btn"
              @click.stop="$emit('close-cycle', cycle)"
            >
              <img src="/img/icons/fbc-icons/CloseCycle.svg" alt="Close" class="cycle-table-row__close-icon mr-1">
              <span class="cycle-table-row__cycle-action-text">Close Cycle</span>
            </v-btn>

            <UpsiRowActions
              v-if="showUpsiRowActions"
              :cycle="cycle"
              :can-publish-right="canPublishUpsi"
              :can-review-right="canReviewUpsi"
              :can-reopen="showReopenAction"
              :index="index"
              @publish="$emit('upsi-publish', cycle, $event)"
              @review="$emit('upsi-review', cycle)"
              @reopen-cycle="$emit('reopen-cycle', cycle)"
            />

            <v-btn
              v-if="showLegacyReopenAction"
              small
              text
              class="cycle-table-row__cycle-action text-none"
              data-testid="bookclosure-project-list__reopen-btn"
              @click.stop="$emit('reopen-cycle', cycle)"
            >
              <v-icon small data-testid="bookclosure-project-list__reopen-icon" class="mr-1">mdi-lock-open-outline</v-icon>
              <span class="cycle-table-row__cycle-action-text">Reopen Cycle</span>
            </v-btn>
          </template>
        </div>

        <div class="cycle-table-row__actions-more">
          <v-menu
            offset-y
            left
            :close-on-content-click="true"
            content-class="cycle-table-row__more-menu-panel"
            z-index="10"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-tooltip left content-class="tooltip-left" open-delay="500">
                <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                  <v-btn
                    icon
                    small
                    class="cycle-table-row__more-btn"
                    aria-label="More actions"
                    v-bind="{ ...attrs, ...tooltipAttrs }"
                    v-on="{ ...on, ...tooltipOn }"
                    data-testid="cycles-page__more-btn"
                  >
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <span>More actions</span>
              </v-tooltip>
            </template>

            <div class="cycle-table-row__more-menu" data-testid="cycles-page__more-menu">
              <button
                v-if="showEditAction"
                type="button"
                class="cycle-table-row__more-item"
                data-testid="bookclosure-project-list__edit-btn"
                @click.stop="onEdit"
              >
                <span class="cycle-table-row__more-icon-wrap">
                  <img
                    src="/img/icons/fbc-icons/CycleMenuEdit.svg"
                    alt=""
                    class="cycle-table-row__more-icon-img"
                  />
                </span>
                <span class="cycle-table-row__more-label">Edit</span>
              </button>

              <button
                v-if="isManager && !isSettingUp && !isFailedSetup"
                type="button"
                class="cycle-table-row__more-item"
                :data-testid="cycle.notification
                  ? 'bookclosure-project-list__disable-notification-btn'
                  : 'bookclosure-project-list__enable-notification-btn'"
                @click.stop="onToggleNotification"
              >
                <span class="cycle-table-row__more-icon-wrap">
                  <img
                    src="/img/icons/fbc-icons/CycleMenuEmail.svg"
                    alt=""
                    class="cycle-table-row__more-icon-img"
                  />
                </span>
                <span class="cycle-table-row__more-label">
                  {{ cycle.notification ? 'Disable Email Notification' : 'Enable Email Notification' }}
                </span>
              </button>

              <button
                type="button"
                class="cycle-table-row__more-item"
                data-testid="bookclosure-project-list__activity-log-btn"
                @click.stop="openActivityLog"
              >
                <span class="cycle-table-row__more-icon-wrap">
                  <img
                    src="/img/icons/fbc-icons/CycleMenuActivityLog.svg"
                    alt=""
                    class="cycle-table-row__more-icon-img"
                  />
                </span>
                <span class="cycle-table-row__more-label">Activity Log</span>
              </button>

              <button
                v-if="exportManagerEnabled && canExportActivityLogs"
                type="button"
                class="cycle-table-row__more-item"
                data-testid="bookclosure-project-list__export-manager-btn"
                @click.stop="openExportManager"
              >
                <span class="cycle-table-row__more-icon-wrap">
                  <img
                    src="/img/icons/fbc-icons/CycleMenuExportManager.svg"
                    alt=""
                    class="cycle-table-row__more-icon-img"
                  />
                </span>
                <span class="cycle-table-row__more-label">Export Manager</span>
              </button>
            </div>
          </v-menu>
        </div>
      </div>

      <!-- Dialogs sit outside the flex row so they never shift ⋯ alignment -->
      <ActivityLog
        v-if="activityLogMounted"
        ref="activityLog"
        hide-trigger
        by-cycle
        :resource-id="cycle._id"
        :row-description="cycle.description"
      />
      <CycleErrorDetail
        v-if="isFailedSetup"
        ref="cycleErrorDetail"
        hide-trigger
        :error-message="cycleErrorMessage"
      />
      <ExportManagerDialog
        v-if="exportManagerMounted && exportManagerEnabled && canExportActivityLogs"
        v-model="exportManagerOpen"
        :cycle-id="cycleId"
        :can-export="canExportActivityLogs"
        :can-export-file="isController"
      />
    </td>
  </tr>
</template>

<script>
import './CycleTableRow.scss';
import { ActivityLog } from '../../../../components';
import CycleErrorDetail from '../../../../components/CycleErrorDetail/CycleErrorDetail.vue';
import { ExportManagerDialog } from '../../modals';
import UpsiRowActions from '../upsi/UpsiRowActions.vue';
import UpsiStatusCell from '../upsi/UpsiStatusCell.vue';
import { CYCLE_CREATION_STATUS, CYCLE_STAGES } from '../../../../utils/constants';
import {
  calcCompletion,
  canExportCycleActivityLogs,
  formatPeriodRange,
  isActorController,
  isCycleManager,
  getCycleStatusConfig,
  hasFullCycleAccess as userHasFullCycleAccess,
  isCycleSettingUp,
  displayedUpsiStage,
  upsiActionsReplaceReopen,
} from '../../utils/cyclesHelpers';

export default {
  name: 'CycleTableRow',

  components: {
    ActivityLog,
    CycleErrorDetail,
    ExportManagerDialog,
    UpsiRowActions,
    UpsiStatusCell,
  },

  props: {
    cycle: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    userid: {
      type: String,
      default: '',
    },
    locationsMap: {
      type: Object,
      default: () => ({}),
    },
    actorRoleLabel: {
      type: String,
      default: '',
    },
    actorLocationIds: {
      type: Array,
      default: () => [],
    },
    right: {
      type: String,
      default: 'financialclose_project',
    },
    upsiEnabled: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      activityLogMounted: false,
      exportManagerMounted: false,
      exportManagerOpen: false,
      rowHovered: false,
    };
  },

  computed: {
    cycleId() {
      const id = this.cycle && this.cycle._id;
      return id == null ? '' : String(id);
    },

    visibleHeaders() {
      return (this.headers || []).filter((h) => h.visible !== false);
    },

    isManager() {
      return isCycleManager(this.cycle, this.userid);
    },

    canExportActivityLogs() {
      return canExportCycleActivityLogs({
        cycle: this.cycle,
        userId: this.userid,
        actorRoleLabel: this.actorRoleLabel,
        actorLocationIds: this.actorLocationIds,
      });
    },

    /**
     * Same audience as Export Manager: a controller, or a cycle manager
     * assigned every location on the cycle. The API `can_request` flag still applies.
     */
    canPublishUpsi() {
      return this.canExportActivityLogs;
    },

    canReviewUpsi() {
      return this.isController;
    },

    isController() {
      return isActorController(this.actorRoleLabel);
    },

    exportManagerEnabled() {
      return !!this.$store.getters['tasks/getFormDataV3']?.exportManagerEnabled;
    },

    hasFullCycleAccess() {
      return userHasFullCycleAccess({
        cycle: this.cycle,
        userId: this.userid,
        actorLocationIds: this.actorLocationIds,
      });
    },

    isClosed() {
      return this.cycle.stage === CYCLE_STAGES.CLOSED;
    },

    isSettingUp() {
      return isCycleSettingUp(this.cycle.cycle_creation_status);
    },

    isFailedSetup() {
      return this.cycle.cycle_creation_status === CYCLE_CREATION_STATUS.FAILED;
    },

    cycleErrorMessage() {
      return (
        this.cycle.cycle_creation_error_message ||
        this.cycle.error_message ||
        ''
      );
    },

    showCreationBadge() {
      const status = this.cycle.cycle_creation_status;
      return (
        status &&
        status !== CYCLE_CREATION_STATUS.COMPLETED &&
        status !== CYCLE_CREATION_STATUS.PARTIALLY_COMPLETED
      );
    },

    statusConfig() {
      return getCycleStatusConfig(this.cycle.cycle_creation_status);
    },

    summary() {
      return this.cycle.summary || {};
    },

    completionPct() {
      return this.cycle.completionPct != null
        ? this.cycle.completionPct
        : calcCompletion(this.summary);
    },

    locationIds() {
      return this.cycle.location || [];
    },

    primaryLocationName() {
      if (!this.locationIds.length) return '';
      const id = this.locationIds[0];
      const loc = this.locationsMap[id];
      if (loc && loc.name) return loc.name;
      if (this.cycle.locationNames && this.cycle.locationNames[0]) {
        return this.cycle.locationNames[0];
      }
      return id;
    },

    extraLocationCount() {
      return Math.max(0, this.locationIds.length - 1);
    },

    extraLocationNames() {
      return this.locationIds.slice(1).map((id) => {
        const loc = this.locationsMap[id];
        return (loc && loc.name) || id;
      });
    },

    canEdit() {
      return this.$nova && this.$nova.hasRight(this.right, 'edit');
    },

    canClose() {
      return this.$nova && this.$nova.hasRight(this.right, 'close');
    },

    canOpen() {
      return this.$nova && this.$nova.hasRight(this.right, 'open');
    },

    showCloseAction() {
      return this.hasFullCycleAccess && !this.isClosed && this.canClose;
    },

    showReopenAction() {
      return this.isManager && this.isClosed && this.canOpen;
    },

    displayedUpsiState() {
      const state = (this.cycle && this.cycle.upsi_state) || {};
      return { ...state, stage: displayedUpsiStage(this.cycle) };
    },

    showUpsiRowActions() {
      return this.upsiEnabled
        && this.isClosed
        && !this.isSettingUp
        && !this.isFailedSetup
        && upsiActionsReplaceReopen(this.cycle, this.canPublishUpsi);
    },

    showLegacyReopenAction() {
      if (!this.showReopenAction) return false;
      return !this.showUpsiRowActions;
    },

    showEditAction() {
      return (
        this.isManager &&
        !this.isClosed &&
        !this.isSettingUp &&
        !this.isFailedSetup &&
        this.canEdit
      );
    },
  },

  beforeDestroy() {
    this.rowHovered = false;
  },

  methods: {
    formatPeriodRange,

    cellClass(headerValue) {
      const classes = [];
      if (headerValue === 'description') {
        classes.push('description-header');
      }
      if (
        this.isSettingUp &&
        headerValue !== 'status' &&
        headerValue !== 'progress'
      ) {
        classes.push('grey--text', 'text--darken-2');
      }
      return classes;
    },

    onRowClick() {
      this.$emit('click', this.cycle);
    },

    onEdit() {
      this.$emit('edit-cycle', this.cycle);
    },

    async openActivityLog() {
      this.activityLogMounted = true;
      await this.$nextTick();
      this.$refs.activityLog?.openDialog();
    },

    async openExportManager() {
      this.exportManagerMounted = true;
      await this.$nextTick();
      this.exportManagerOpen = true;
    },

    openCycleErrorDetail() {
      this.$refs.cycleErrorDetail?.openDialog();
    },

    onCreationBadgeClick() {
      if (this.isFailedSetup) {
        this.openCycleErrorDetail();
      }
    },

    onToggleNotification() {
      this.$emit('toggle-notification', {
        id: this.cycle._id,
        type: this.cycle.notification ? 'Disable' : 'Enable',
        cycle: this.cycle,
      });
    },
  },
};
</script>
 